import { h, Host } from "@stencil/core";
import { SwitchLabelPlacement } from "./switch-input-types";
export class SwitchInput {
    constructor() {
        this.disabled = false;
        this.checked = false;
        this.name = undefined;
        this.label = undefined;
        this.desc = undefined;
        this.labelPlacement = SwitchLabelPlacement.Start;
    }
    /**
     * Event fired when the switch's on/off state changes.
     * This event is composed, which means it will bubble through the shadow DOM boundary.
     * It is also cancelable, so the default action can be prevented.
     * When the event is dispatched, it bubbles up through the DOM tree.
     *
     * The event's detail is the new boolean value of the switch's state.
     *
     * @event uxChange
     */
    didToggle;
    componentWillLoad() {
        if (!this.label) {
            // eslint-disable-next-line no-console
            console.warn("Accessibility error: 'label' property is required for screen reader identification.");
        }
    }
    toggle = (e) => {
        e.preventDefault();
        const { disabled } = this;
        if (disabled) {
            return;
        }
        const on = !this.checked;
        const toggleEvent = this.didToggle.emit(on);
        if (toggleEvent.defaultPrevented) {
            return;
        }
        this.checked = on;
    };
    render() {
        return (h(Host, { key: 'd0b0d288d22eeb1136fa82ecddfbf01516538d12', checked: this.checked, disabled: this.disabled, labelPlacement: this.labelPlacement }, h("label", { key: '14cdbee12893820ef21d60c7f9582a96650bfa8a' }, this.label && h("span", { key: '94e3fa0e2922aa4d48ef0f36c4c32af7dc93e5b5', id: "inner-label" }, this.label), h("button", { key: 'afa30208bb624327b2b6db7daa1e8717b398f9d1', role: "switch", "aria-checked": this.checked ? "true" : "false", tabIndex: this.disabled ? -1 : 0, type: "button", disabled: this.disabled, onClick: this.toggle }, h("span", { key: '9e84a2f32b96eaab35631b7c6fb787319b78e4e4', id: "handle" }))), this.desc && h("div", { key: '3da4d802880a7a63e906cbafaef43cca1bfcb632', id: "description" }, this.desc)));
    }
    static get is() { return "ux-switch-input"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["switch-input.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["switch-input.css"]
        };
    }
    static get properties() {
        return {
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "false"
                        }],
                    "text": "Indicates if the switch is disabled.\nThe switch will be in a non-interactive state if this is set to true."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "checked": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "false"
                        }],
                    "text": "Determines whether the switch is checked or not.\nThis is a mutable property that can be changed programmatically.\nReflects the state to the component's attribute for CSS styling."
                },
                "attribute": "checked",
                "reflect": true,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The name of the switch component.\nCan be used to identify the component when used in forms or scripting."
                },
                "attribute": "name",
                "reflect": false
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The label associated with the switch component.\nProvides a textual label for screen readers and for better accessibility."
                },
                "attribute": "label",
                "reflect": false
            },
            "desc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A description for the switch component.\nThis can be additional information that is not included in the label."
                },
                "attribute": "desc",
                "reflect": false
            },
            "labelPlacement": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "SwitchLabelPlacement",
                    "resolved": "SwitchLabelPlacement.End | SwitchLabelPlacement.Start",
                    "references": {
                        "SwitchLabelPlacement": {
                            "location": "import",
                            "path": "./switch-input-types",
                            "id": "src/components/switch-input/switch-input-types.ts::SwitchLabelPlacement"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "SwitchLabelPlacement.Start"
                        }],
                    "text": "The position of the label relative to the switch.\nCan be 'start' or 'end', which places the label before or after the switch."
                },
                "attribute": "label-placement",
                "reflect": false,
                "defaultValue": "SwitchLabelPlacement.Start"
            }
        };
    }
    static get events() {
        return [{
                "method": "didToggle",
                "name": "uxChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "event",
                            "text": "uxChange"
                        }],
                    "text": "Event fired when the switch's on/off state changes.\nThis event is composed, which means it will bubble through the shadow DOM boundary.\nIt is also cancelable, so the default action can be prevented.\nWhen the event is dispatched, it bubbles up through the DOM tree.\n\nThe event's detail is the new boolean value of the switch's state."
                },
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                }
            }];
    }
}
//# sourceMappingURL=switch-input.js.map
